// material ui
import { Grid, Tab, Tooltip, Typography } from '@material-ui/core';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import { withStyles } from '@material-ui/core/styles';
import MuiTabs from '@material-ui/core/Tabs';
import CircleIcon from '@material-ui/icons/Brightness1';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import i18next from 'i18next';
import React from 'react';

// api
// components
import { IAccordionPanel } from '~/src/dash/components/accordion/multi-accordion';
import { IInputRow, InputRow } from '~/src/dash/components/input-row';
import { createChipWihFadingBackgroundVariations } from '~/src/dash/components/model-chip';
import { Col, Cols, spacing, Text } from '~/src/dash/publicaUI';
// other
import { IWithStylesProps } from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
// utils
import { decorate } from '~/src/dash/utils/decorator';
import { displayLimitedItems, formatForClassname } from '~/src/dash/utils/format';
import { smallRadius } from '~/src/dash/utils/material-dashboard-react';
import { ct, isLanguageOversized } from '~/src/locales/common';
// views

// COMPONENT DESCRIPTION: Accordion Panel renders the parent panel and its children rows.
// CustomRender components are housed in this file.

interface IProps extends IWithStylesProps {
  // panel to render
  panel: IAccordionPanel;
  // is this panel expanded
  isExpanded: boolean;
  // disableEdit if the user isnt super, has no edit rights, etc.
  disableEdit: boolean;
  // disable active tags for individual rows
  disableActiveTags?: boolean;
  // handler to open and close this panel
  handleExpandPanel: (panelTitle: string) => void;
}

interface IState {
  // tracks the tab index inside the panel
  currentIndex: number;
}
const DARKGREEN = '#09AE82';

export const ChipActive = createChipWihFadingBackgroundVariations(
  '#00C488',
  'rgba(0,196,136,0.10)',
  'rgba(0,196,136,0.01)',
  'rgba(0,196,136,0.15)',
);

export const ChipCustom = createChipWihFadingBackgroundVariations(
  '#5865E8',
  'rgba(88,101,232,0.10)',
  'rgba(88,101,232,0.05)',
  'rgba(88,101,232,0.15)',
);

const Accordion = withStyles({
  root: {
    border: `1px solid ${publicaColors.lightGray}`,
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: 'auto',
      minHeight: 36,
    },
  },
  expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles({
  root: {
    height: 40,
    'min-height': 40,
    '&$expanded': {
      height: 40,
      minHeight: 40,
    },
  },
  expanded: {
    margin: 'auto',
    minHeight: 40,
  },
})(MuiAccordionSummary);

const AccordionDetails = withStyles({
  root: {
    width: '100%',
    padding: '0px 16px 16px',
  },
})(MuiAccordionDetails);

const Tabs = withStyles({
  root: {
    maxWidth: 1000,
    paddingRight: 40,
    height: 44,
    minHeight: 40,
    marginBottom: 5,
  },
  indicator: {
    color: '#4952FF',
    backgroundColor: '#4952FF',
  },
})(MuiTabs);

export const RenderEndpointScorecardPanelHeaders = () => {
  return (
    <Grid
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        width: '100%',
        fontSize: 13,
        alignItems: 'start',
      }}
    >
      <Typography
        style={{
          justifySelf: 'start',
          fontWeight: 'bold',
          marginLeft: 5,
          fontSize: 13,
        }}
      >
        Parameter
      </Typography>
      <Grid />
      <Grid
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography
          style={{
            fontWeight: 'bold',
            margin: 0,
            fontSize: 13,
          }}
        >
          % of occurence within last 24hr
        </Typography>
        <Typography
          style={{
            backgroundColor: 'rgb(245, 245, 245)',
            margin: 0,
            textAlign: 'center',
            display: 'inline-block',
            padding: 5,
            borderRadius: smallRadius,
            fontWeight: 400,
            fontSize: 13,
          }}
        >
          Must be over 50%
        </Typography>
      </Grid>
    </Grid>
  );
};

export const RenderEndpointScorecardPanel = (props: any) => {
  return (
    <Grid
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        width: '100%',
        alignItems: 'center',
      }}
    >
      <Typography
        style={{
          justifySelf: 'start',
          margin: 5,
          fontSize: '0.875rem',
        }}
      >
        {props.title1}
      </Typography>
      <Grid />
      <Grid container style={{ display: 'flex', justifyContent: 'center' }}>
        {props.percent >= 50 ? (
          <CheckCircleIcon
            style={{
              fontSize: '0.875rem',
              alignSelf: 'center',
              color: DARKGREEN,
            }}
          />
        ) : (
          <CircleIcon
            style={{
              fontSize: '0.875rem',
              alignSelf: 'center',
              color: 'rgb(245, 245, 245)',
            }}
          />
        )}
        <Typography
          style={{
            color: props.percent >= 50 ? DARKGREEN : '#757575',
            borderRadius: smallRadius,
            fontWeight: 500,
            margin: 5,
            fontSize: '0.875rem',
          }}
        >
          {Math.round(props.percent * 100) / 100}%
        </Typography>
      </Grid>
    </Grid>
  );
};

export const MultiAccordionPanel = decorate(
  {
    styles: () => ({
      accordionTitle: {
        fontSize: 14,
        fontWeight: 500,
      },
      accordionPanelHeaderText: {
        marginLeft: 36,
        fontSize: 12,
        fontWeight: 400,
        color: '$434343',
      },
      accordionPanelHeaderTextActive: {
        marginLeft: 36,
        fontSize: 12,
        fontWeight: 500,
        color: '#212529',
      },
      accordionRow: {
        marginLeft: 24,
        marginRight: 24,
        width: '100%',
      },
      accordionPanelHeader: {
        width: '90%',
      },
      accordionPanelErrorText: {
        fontSize: 14,
        fontWeight: 500,
        color: 'red',
      },
      indicator: {
        color: '#4952FF',
        backgroundColor: '#4952FF',
      },
    }),
  },
  class extends React.Component<IProps, IState> {
    state = {
      currentIndex: 0,
    };

    generateRowTags = (): [string[], boolean] => {
      if (this.props.panel.enableTabs && this.props.panel.tabRows) {
        const rowTags = [];
        for (const tabRow of this.props.panel.tabRows) {
          for (const row of tabRow.rows) {
            const rowActive = row.isActiveValue1();
            if (rowActive) {
              rowTags.push(row.getActiveTag1(rowActive));
            }
          }
        }
        return [rowTags, rowTags.length > 0];
      }
      const { panel } = this.props;

      let rowTags = [];
      // if any row has an active value, the panel adds the active/custom chip
      let anyRowActive = false;
      // add all active tags
      for (const row of panel.rows) {
        const rowActive = row.isActiveValue1();
        const rowTag = row.getActiveTag1(rowActive);
        // if a row has an ActiveTag that isn't empty string, add it to the row tag
        if (rowActive && rowTag !== '') {
          rowTags.push(rowTag);
          anyRowActive = true;
        }
      }
      // once all active tags are added, add the passive tags (i.e. All Channels, All Device Types)
      // if no active tags are present
      if (rowTags.length === 0) {
        for (const row of panel.rows) {
          const rowActive = row.isActiveValue1();
          const rowTag = row.getActiveTag1(rowActive);
          if (!rowActive && rowTag !== '') {
            rowTags.push(rowTag);
          }
        }
      }
      // hardcore for Remove Content
      if (rowTags.includes('Remove Content')) {
        rowTags = ['Remove Content'];
      }

      // if no tags are added, use default inactive tag for panel
      if (rowTags.length === 0) {
        rowTags.push(panel.panelInactiveRowTag);
      }
      return [rowTags, anyRowActive];
    };

    handleChangeTab = (currentIndex: number): void => {
      this.setState({
        currentIndex,
      });
    };

    renderAccordionPanel() {
      const { panel, disableEdit, classes } = this.props;
      if (panel.enableTabs && panel.tabRows) {
        const currentPanel = panel.tabRows[this.state.currentIndex];
        // In case we are currently viewing a conditionally rendered tab,
        // revert to the first tab if the conditionally rendered tab is no longer viewable
        if (!currentPanel) {
          this.setState({
            currentIndex: 0,
          });
          return;
        }
        return (
          <>
            {panel.StaticTooltip && (
              <div style={{ padding: '16px 44px 24px 44px', width: '100%' }}>
                <panel.StaticTooltip />
              </div>
            )}
            <AccordionDetails>
              <Grid container direction='column'>
                <Tabs value={this.state.currentIndex} variant='scrollable'>
                  {panel.tabRows.map((tabRow, i) => {
                    const tabSelected = i === this.state.currentIndex;
                    let color = tabSelected ? '#4952FF' : '#757575';
                    for (const row of tabRow.rows) {
                      if (row.isActiveValue1() && !tabSelected) {
                        color = 'black'; // black font indicates tab has active value
                      }
                    }
                    return (
                      <Tab
                        onClick={() => this.handleChangeTab(i)}
                        value={i}
                        label={tabRow.tabName}
                        style={{
                          color,
                          textTransform: 'none',
                          padding: 0,
                          height: 30,
                          minWidth: 115,
                        }}
                      />
                    );
                  })}
                </Tabs>
                {/* Panel Rows */}
                {currentPanel && (
                  <Grid
                    container
                    direction='column'
                    justify='flex-start'
                    alignItems='flex-end'
                    spacing={1}
                    className={classes.accordionRow}
                  >
                    {currentPanel.rows.map((row: IInputRow, idx) => (
                      <InputRow
                        key={`input-row-${row.title1 ? row.title1 : idx}`} // using index to identify if the route does not have a name yet
                        rowInfo={row}
                        disableEdit={disableEdit}
                      />
                    ))}
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </>
        );
      } else {
        // render panel without tabs
        return (
          <>
            {panel.StaticTooltip && (
              <div
                style={{
                  padding: '16px 44px 24px 44px',
                  width: '100%',
                }}
              >
                <panel.StaticTooltip />
              </div>
            )}
            <AccordionDetails style={{ width: '100%' }}>
              {/* Panel Rows */}
              <Grid
                container
                direction='column'
                justify='flex-start'
                alignItems='flex-end'
                spacing={1}
                style={
                  panel.endpointScorecard
                    ? {
                        border: `1px solid ${publicaColors.lightGray}`,
                        borderRadius: smallRadius,
                        padding: 10,
                        width: '80%',
                      }
                    : {}
                }
                className={classes.accordionRow}
              >
                {panel.rows.map((row: IInputRow) => (
                  <InputRow rowInfo={row} disableEdit={disableEdit} />
                ))}
                {panel.optionalError && (
                  <Grid item style={{ width: '100%', marginBottom: 5, marginTop: 5 }}>
                    <Typography className={classes.accordionPanelErrorText}>
                      {panel.optionalError}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </>
        );
      }
    }

    render() {
      const { panel, isExpanded, handleExpandPanel, classes, disableActiveTags } = this.props;
      const [rowTags, anyRowActive] = this.generateRowTags();

      let chip = <ChipActive label={panel.panelActiveChipTitle} />;
      if (panel.panelActiveChipTitle === ct('Custom')) {
        chip = <ChipCustom label={panel.panelActiveChipTitle} />;
      }

      return (
        <Accordion expanded={isExpanded} onChange={() => handleExpandPanel(panel.title)}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls='panel1a-content'
            id='panel1a-header'
            className={`cypress-multi-accordion-panel_accordionsummary-${formatForClassname(
              panel.title,
            )}`}
          >
            {/* Panel Header and Tags */}
            {panel.endpointScorecard ? (
              <Grid
                container
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                }}
                className={classes.accordionPanelHeader}
              >
                <Typography className={classes.accordionTitle} style={{ width: '100%' }}>
                  {panel.title}
                </Typography>
                <Grid
                  container
                  style={{
                    justifyContent: 'flex-end',
                  }}
                >
                  <Typography
                    style={{
                      paddingRight: 2.5,
                      backgroundColor:
                        panel.percentCompleted !== 0 ? 'rgba(157, 251, 226,0.4)' : '',
                      color: panel.percentCompleted !== 0 ? DARKGREEN : '',
                      fontWeight: 400,
                      borderRadius: smallRadius,
                      padding: '2.5px 5px',
                      fontSize: '0.875rem',
                    }}
                  >
                    {panel.percentCompleted
                      ? `${panel.percentCompleted}%`
                      : panel.percentCompleted === 0
                      ? '0%'
                      : ''}
                  </Typography>
                  <Tooltip
                    title={`Add params within this level to improve your score by ${panel.percentLeft}%`}
                  >
                    <Typography
                      style={{
                        padding: 2.5,
                        color: panel.paramsLeft === 0 ? 'black' : '#757575',
                        fontSize: '0.875rem',
                        fontWeight: panel.paramsLeft === 0 ? 400 : undefined,
                      }}
                    >
                      {panel.paramsLeft === 1
                        ? ` - 1 Param Left (${Math.round((panel.percentLeft || 0) * 100) / 100}%)`
                        : panel.paramsLeft === 0
                        ? ' - Completed'
                        : ` - ${panel.paramsLeft} Params Left (${
                            Math.round((panel.percentLeft || 0) * 100) / 100
                          }%)`}
                    </Typography>
                  </Tooltip>
                </Grid>
              </Grid>
            ) : (
              <Cols
                gap={spacing.small}
                style={{
                  width: '100%',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                {/* Panel Title */}
                <Col flex>
                  <Text
                    small={!isLanguageOversized(i18next.language)}
                    extraSmall={isLanguageOversized(i18next.language)}
                    bold
                  >
                    {panel.title}
                  </Text>
                </Col>
                {/* Panel Header Text */}
                <Col flex className={`cypress-test-selected-${formatForClassname(panel.title)}`}>
                  <Text
                    small={!isLanguageOversized(i18next.language)}
                    extraSmall={isLanguageOversized(i18next.language)}
                    bold
                    color={anyRowActive ? 'inherit' : publicaColors.darkGray}
                  >
                    {disableActiveTags ? '' : displayLimitedItems({ items: rowTags })}
                  </Text>
                </Col>
                {/* Chip */}
                <Col flex style={{ textAlign: 'center' }}>
                  {anyRowActive && chip}
                </Col>
              </Cols>
            )}
          </AccordionSummary>
          {this.renderAccordionPanel()}
        </Accordion>
      );
    }
  },
);
